import { LivenessProgress } from "./SessionUIController/types";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useLocalizedStrings } from "../../Localization";
import { AidKitLogo, BarSpinner, SpacedSpinner } from "../../Util";
import { CardFront, DebitCardFront, CardBack, Selfie } from "./CoverImages";
import { facingModeForStage } from "./consts";
import { useEffect, useState } from "react";
import { Button } from "@aidkitorg/component-library";
import { useLivenessDetectionSessionUIContext } from "./SessionUIController";

type FlowState = "pending" | "failed" | "passed" | "max_attempts_reached" | undefined;

// FinalPage can update flow state for an individual part to "uploading" for more specific localization
type FinalFlowState = "uploading" | "failed_to_upload" | FlowState;

export function FinalPage(props: {
  multipleCameras: boolean, 
  canCloseWindow: boolean,
  setRetrying: React.Dispatch<React.SetStateAction<boolean>>,
}) {

  const { livenessProgress, livenessConfig, realtimeResults, setRealtimeResults, setActionState, setFacingMode, setLivenessProgress } = useLivenessDetectionSessionUIContext();
  const L = useLocalizedStrings();

  const parts = livenessConfig!.identification ?
    ['front', 'back', 'selfie'] as const :
    ['selfie'] as const;

  // Figure out the global state to display
  const [state, setState] = useState(L.questions.identity.please_wait_processing);
  useEffect(() => {
    let state = L.questions.identity.please_wait_processing;
    let completeCount = 0;
    let failedCount = 0;
    // console.log({ livenessProgress, realtimeResults });
    parts.forEach((part) => {
      if ((livenessProgress)[part].videoUpload !== 1 || (livenessProgress)[part].imageUpload !== 1) return;
      if ((realtimeResults)?.[part]?.state === 'passed' || (realtimeResults)?.[part]?.state === 'max_attempts_reached') completeCount += 1;
      if ((realtimeResults)?.[part]?.state === 'failed') failedCount += 1;
    });
    console.log("FCT:", failedCount, completeCount, parts.length);

    if (completeCount === parts.length) {
      state = props.canCloseWindow 
        ? L.applicant.all_done_you_may_now_close_this_window 
        : L.questions.identity.please_wait_processing;
      setActionState("done");
    }
    if (failedCount > 0) {
      state = L.questions.identity.blurry_sorry;
    }
    setState(state);
  }, [props.canCloseWindow, livenessProgress, realtimeResults]);

  return <div className="bg-gray-100 flex flex-col fixed h-screen w-screen overflow-y-scroll" style={{ height: '-webkit-fill-available' }}>
    <div className="flex items-center flex flex-col py-2">
      <div className="px-10 py-10 text-xl m-0 bg-white w-full flex items-center flex-col">
        <div className="max-w-lg flex-1">
          <AidKitLogo width={100} height={50} />
          <h3>{state}</h3>
          <div>{state === L.questions.identity.please_wait_processing ? 
            <BarSpinner /> : null}</div>
        </div>
      </div>
    </div>
    {state === L.applicant.all_done_you_may_now_close_this_window && <div className="flex flex-none items-center justify-center">
      <Button variant="primary" onClick={(e) => {
        e.preventDefault();
        window.close(); 
      }}
      >{L.apply.close}</Button>
    </div>}
    <div className="flex flex-grow bg-gray-100 text-gray-800 justify-center items-center">
      <div className="mx-auto max-w-lg px-4 py-4 sm:px-6 md:py-16">
        <div>
          <ul role="list" className="divide-y divide-gray-200">
            {parts.map((part) => (
              <li key={"progress-" + part} className="flex items-center justify-between gap-x-2 p-2 bg-white rounded-md m-3" style={{ "border": '2px solid black' }}>
                <div className="flex min-w-0 gap-x-4 text-center" style={{ width: '90px' }}>
                  <div className="text-center" style={{ 'width': '90px' }}>
                    {{
                      'front': <CardFront style={{ height: 50, width: 'auto' }} />,
                      'back': livenessConfig?.identification?.back.alternativeDocument?.kind === 'Debit Card' 
                        ? <DebitCardFront style={{ height: 50, width: 'auto' }} /> 
                        : <CardBack style={{ height: 50, width: 'auto' }} />,
                      'selfie': <Selfie style={{ height: 50, width: 'auto', display: 'inline-block' }} />,
                    }[part]}
                  </div>
                </div>
                <div className="min-w-0 flex-auto mr-2 flex justify-center items-center text-gray-900">
                  {(() => {
                    let partState = realtimeResults?.[part]?.state as FinalFlowState;
                    if (livenessProgress[part].videoUpload !== 1 || livenessProgress[part].imageUpload !== 1) {
                      partState = 'uploading';
                    }
                    if (livenessProgress[part]?.failedToUpload) {
                      partState = 'failed_to_upload';
                    }
                    switch (partState) {
                      case 'failed':
                        return L.questions.identity.photo_too_blurry;
                      case 'passed':
                      case 'max_attempts_reached':
                        return L.questions.identity.complete;
                      case 'uploading':
                        return L.questions.attachment.uploading + " " + Math.round(Math.min(livenessProgress[part].imageUpload || 0, livenessProgress[part].videoUpload || 0)*100) + '%';
                      case 'failed_to_upload':
                        return L.liveness.failed_to_upload;
                      case 'pending':
                      default:
                        return L.questions.identity.checking_quality;
                    }
                  })()}
                </div>
                <div className="text-center mr-2" style={{ width: "70px" }}>
                  {(() => {
                    let partState = realtimeResults?.[part]?.state as FinalFlowState;
                    if (livenessProgress[part].videoUpload !== 1 || livenessProgress[part].imageUpload !== 1) {
                      partState = 'pending';
                    } 

                    if (livenessProgress[part]?.failedToUpload) {
                      partState = 'failed_to_upload';
                    }
                    switch (partState) {
                      case 'failed_to_upload':
                      case 'failed':
                        return <button className="border-0 justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                          onClick={() => {
                            props.setRetrying(true);

                            // Reset the progress for this part
                            setLivenessProgress((cur) => ({
                              ...cur,
                              [part]: {
                                ...cur[part],
                                failedToUpload: false,
                                imageUpload: 0,
                                videoUpload: 0
                              }
                            }))
                            
                            setRealtimeResults((cur) => ({
                              ...cur, 
                              [part]: { state: "pending" }
                            }));

                            partState = 'pending';

                            setActionState(part);

                            if (props.multipleCameras) setFacingMode(facingModeForStage[part])
                          }}>{L.questions.identity.retry}</button>;
                      case 'passed':
                      case 'max_attempts_reached': // ¯\_(ツ)_/¯
                        return <span><CheckCircleIcon className="text-green-500" width={30} /></span>;
                      case 'pending':
                      default:
                        return <span><SpacedSpinner /></span>;
                    }
                  })()}
                </div>
              </li>
            ))}
          </ul></div>
      </div>
    </div>
  </div>;
}
